import { useState, useRef } from 'react';
import { audioBufferToWav, getSupportedAudioMimeType } from '../utils/audioUtils';

export const useAudioRecording = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingError, setRecordingError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const startTimeRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);

  const convertToWav = async (audioBlob) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const wav = audioBufferToWav(audioBuffer);
      return new Blob([wav], { type: 'audio/wav' });
    } catch (error) {
      console.error('Error converting to WAV:', error);
      return audioBlob;
    }
  };

  const startRecording = async (onRecordingComplete) => {
    setRecordingError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setRecordingError('Microphone access is not available. Please use HTTPS or localhost.');
        return;
      }
      if (typeof MediaRecorder === 'undefined' || !window.MediaRecorder) {
        setRecordingError('Recording is not supported in this browser. Try iOS 14+ or Chrome/Firefox.');
        return;
      }

      // Check if any audio input devices exist before requesting permission
      if (navigator.mediaDevices?.enumerateDevices) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const audioInputs = devices.filter(d => d.kind === 'audioinput');
          if (audioInputs.length === 0) {
            setRecordingError('No microphone found. Please connect an audio input device and try again.');
            return;
          }
        } catch (_) {
          // enumerateDevices can fail in some contexts — proceed and let getUserMedia report the error
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Validate stream has active audio tracks
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0 || !audioTracks[0].enabled) {
        stream.getTracks().forEach(t => t.stop());
        setRecordingError('Microphone is not providing audio. Please check your microphone settings and try again.');
        return;
      }

      // Set up AnalyserNode for real-time waveform visualization
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          const audioCtx = new AudioContextClass();
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 128;
          source.connect(analyser);
          audioContextRef.current = audioCtx;
          analyserRef.current = analyser;
        }
      } catch (e) {
        // Non-fatal: waveform just won't render
        console.warn('Could not create AudioContext for waveform visualization:', e);
      }

      const mimeType = getSupportedAudioMimeType();
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // changed by Cursor: use exact ms duration for submission
        const finalDurationMs = Date.now() - startTimeRef.current;
        const blobType = mediaRecorderRef.current?.mimeType || mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: blobType });

        stream.getTracks().forEach(track => track.stop());

        // Clean up audio context used for waveform
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
          analyserRef.current = null;
        }

        // Save audio data URL
        const reader = new FileReader();
        reader.onloadend = () => {
          setRecordedAudioUrl(reader.result);
        };
        reader.readAsDataURL(audioBlob);

        setRecordedAudio(audioBlob);

        // Convert to WAV and callback
        const wavBlob = await convertToWav(audioBlob);

        const blobUrl = URL.createObjectURL(wavBlob);
        setRecordedAudioUrl(blobUrl);

        if (onRecordingComplete) {
          onRecordingComplete(wavBlob, finalDurationMs, reader.result, blobUrl);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      startTimeRef.current = Date.now();

      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      // Map browser error names to user-friendly messages
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setRecordingError('Microphone permission denied. Please allow microphone access in your browser settings and try again.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        setRecordingError('No microphone found. Please connect an audio input device and try again.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        setRecordingError('Microphone is in use by another application. Please close other apps using the microphone and try again.');
      } else {
        setRecordingError('Failed to access microphone. Please check your device settings and try again.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const cleanup = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
      analyserRef.current = null;
    }
  };

  const reset = () => {
    setRecordedAudio(null);
    setRecordedAudioUrl(null);
    setIsRecording(false);
    setRecordingDuration(0);
    setRecordingError(null);
    audioChunksRef.current = [];
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
      analyserRef.current = null;
    }
  };

  return {
    isRecording,
    recordedAudio,
    recordedAudioUrl,
    recordingDuration,
    recordingError,
    analyserRef,
    startRecording,
    stopRecording,
    cleanup,
    reset,
    setRecordedAudio,
    setRecordedAudioUrl
  };
};
